import { useEffect, useState } from "react";
import { getTransparency } from "../../app/api";
import { Link } from "react-router-dom";
import {
  ShoppingBagIcon,
  UsersIcon,
  CreditCardIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

const HTMLTextView = ({ htmlContent }: any) => {
  if (!htmlContent) return null;
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

export default function Transparency() {
  const [loading, setLoading] = useState(false);
  const [transparencyList, setTransparencyList] = useState<any[]>([]);

  useEffect(() => {
    setLoading(true);
    getTransparency({ level: 1, limit: 50, offset: 0 })
      .then((res: any) => {
        if (res && res.data && res.data.results) {
          setTransparencyList(res.data.results);
        }
      })
      .catch((err) => {
        console.error("Error fetching transparency data:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getIconForTransparency = (name: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes("шилэн") || nameLower.includes("төсөв") || nameLower.includes("санхүү")) {
      return CreditCardIcon;
    }
    if (nameLower.includes("худалдан") || nameLower.includes("авах") || nameLower.includes("тендер")) {
      return ShoppingBagIcon;
    }
    if (nameLower.includes("хүний") || nameLower.includes("нөөц") || nameLower.includes("ажилтан")) {
      return UsersIcon;
    }
    return DocumentTextIcon;
  };

  const getHref = (item: any) => {
    if (item.link) {
      // External link
      if (item.link.startsWith("http")) {
        return item.link;
      }
      // Internal route
      return item.link;
    }
    if (item.pdf_url) {
      return item.pdf_url;
    }
    // Default to detail page if content exists
    if (item.content || item._id) {
      return `/transparency/${item._id}`;
    }
    return "#";
  };

  const isExternalLink = (href: string) => {
    return href && (href.startsWith("http") || href.startsWith("//"));
  };

  return (
    <div className="bg-white py-24 sm:py-32">
      {loading && (
        <div className="w-full h-full fixed top-0 left-0 bg-white opacity-75 z-50">
          <div className="flex justify-center items-center mt-[50vh]">
            <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
              <svg
                aria-hidden="true"
                className="w-24 h-24 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:mx-0">
          <h2 className="text-3xl font-bold tracking-tight text-[#001447] sm:text-4xl italic">
            Ил тод байдал
          </h2>
          <p className="mt-6 text-lg leading-8 text-[#001447] md:w-3/4">
            Мэдээллийг хурдан шуурхай хялбар болгох үүднээс бид хийж буй ажлаа
            нээлттэй, ил тод байлгахыг зорьдог.
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          {transparencyList.length > 0 ? (
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {transparencyList.map((item: any) => {
                const Icon = getIconForTransparency(item.name);
                const href = getHref(item);
                const external = isExternalLink(href);

                return (
                  <div key={item._id || item.id} className="flex flex-col">
                    <dt className="text-base font-bold leading-7 text-gray-900">
                      <div className="mb-6 flex h-10 w-10 items-center justify-center rounded-lg bg-[#08B3EC]">
                        <Icon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </div>
                      {item.name}
                    </dt>
                    <dd className="mt-1 flex flex-auto flex-col text-base leading-7 text-gray-600">
                      {item.content ? (
                        <HTMLTextView htmlContent={item.content.substring(0, 150) + (item.content.length > 150 ? '...' : '')} />
                      ) : (
                        <p className="flex-auto">
                          {item.description || "Дэлгэрэнгүй мэдээллийг харах"}
                        </p>
                      )}
                    </dd>
                    <p className="mt-5">
                      {external ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold leading-6 text-[#08B3EC]"
                        >
                          Харах <span aria-hidden="true">→</span>
                        </a>
                      ) : (
                        <Link to={href}>
                          <a className="text-sm font-semibold leading-6 text-[#08B3EC]">
                            Харах <span aria-hidden="true">→</span>
                          </a>
                        </Link>
                      )}
                    </p>
                  </div>
                );
              })}
            </dl>
          ) : (
            !loading && (
              <div className="text-center text-gray-500 py-12">
                Ил тод байдлын мэдээлэл одоогоор байхгүй байна.
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
